import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Badge } from "../components/ui/feedback";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { Input } from "../components/ui/Input";
import { PageHeader } from "../components/ui/PageHeader";
import { Select } from "../components/ui/Select";
import { adminApi, type AuditFilters } from "./api";
import { DenseTable, QueryError, TD_CLASS, TH_CLASS } from "./dense";
import { auditQueryKey, adminWorkspacesQuery } from "./queries";
import { formatDateTime } from "./format";
import type { ActorKind } from "./types";

const ACTOR_KINDS: readonly ActorKind[] = ["operator", "admin", "player"];

const ACTOR_TONE: Record<ActorKind, "accent" | "red" | "muted"> = {
  operator: "accent",
  admin: "red",
  player: "muted",
};

/** Append-only audit trail with filters in a toolbar. "Load more" follows the server's cursor. */
export function AuditPage() {
  const { t } = useI18n();
  const [workspaceId, setWorkspaceId] = useState("");
  const [actorKind, setActorKind] = useState<ActorKind | "">("");
  const [action, setAction] = useState("");
  const filters: AuditFilters = {
    workspaceId: workspaceId || undefined,
    actorKind: actorKind || undefined,
    action: action.trim() || undefined,
  };
  const workspaces = useQuery(adminWorkspacesQuery({}));

  const audit = useInfiniteQuery({
    queryKey: auditQueryKey(filters),
    queryFn: ({ pageParam }) => adminApi.listAudit({ ...filters, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const rows = audit.data?.pages.flatMap((page) => page.rows) ?? [];

  return (
    <div className="space-y-5">
      <PageHeader title={t("admin.audit.title")} />
      <div className="grid gap-3 sm:grid-cols-3">
        <Select
          aria-label={t("admin.audit.filter.workspace")}
          value={workspaceId}
          onChange={(event) => setWorkspaceId(event.target.value)}
        >
          <option value="">{t("admin.audit.filter.anyWorkspace")}</option>
          {(workspaces.data ?? []).map((workspace) => (
            <option key={workspace.id} value={workspace.id}>
              {workspace.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t("admin.audit.filter.actorKind")}
          value={actorKind}
          onChange={(event) => setActorKind(event.target.value as ActorKind | "")}
        >
          <option value="">{t("admin.audit.filter.anyActor")}</option>
          {ACTOR_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {t(`admin.actor.${kind}`)}
            </option>
          ))}
        </Select>
        <Input
          aria-label={t("admin.audit.filter.action")}
          placeholder={t("admin.audit.filter.action")}
          value={action}
          onChange={(event) => setAction(event.target.value)}
        />
      </div>

      {audit.isError ? (
        <QueryError error={audit.error} onRetry={() => void audit.refetch()} />
      ) : null}
      {audit.isPending ? <p className="text-operate text-ink-3">{t("app.loading")}</p> : null}
      {audit.data && rows.length === 0 ? <EmptyState>{t("admin.empty")}</EmptyState> : null}

      {rows.length > 0 ? (
        <DenseTable label={t("admin.audit.title")} scroll>
          <thead>
            <tr>
              <th scope="col" className={TH_CLASS}>
                {t("admin.col.time")}
              </th>
              <th scope="col" className={TH_CLASS}>
                {t("admin.col.actor")}
              </th>
              <th scope="col" className={TH_CLASS}>
                {t("admin.col.action")}
              </th>
              <th scope="col" className={`${TH_CLASS} hidden md:table-cell`}>
                {t("admin.col.target")}
              </th>
              <th scope="col" className={`${TH_CLASS} hidden md:table-cell`}>
                {t("admin.col.workspace")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-blue-wash/40">
                <td className={`${TD_CLASS} text-note tabular-nums whitespace-nowrap text-ink-2`}>
                  {formatDateTime(row.at)}
                </td>
                <td className={TD_CLASS}>
                  <span className="flex flex-wrap items-center gap-2">
                    <Badge tone={ACTOR_TONE[row.actorKind]}>
                      {t(`admin.actor.${row.actorKind}`)}
                    </Badge>
                    {row.actorId ? (
                      <span className="text-note text-ink-3">{row.actorId}</span>
                    ) : null}
                  </span>
                </td>
                <td className={`${TD_CLASS} font-medium`}>{row.action}</td>
                <td className={`${TD_CLASS} hidden text-ink-2 md:table-cell`}>
                  {row.target ?? t("common.none")}
                </td>
                <td className={`${TD_CLASS} hidden text-ink-2 md:table-cell`}>
                  {row.workspaceId ?? t("common.none")}
                </td>
              </tr>
            ))}
          </tbody>
        </DenseTable>
      ) : null}

      {audit.hasNextPage ? (
        <div className="flex justify-center">
          <Button onClick={() => void audit.fetchNextPage()} loading={audit.isFetchingNextPage}>
            {t("admin.audit.loadMore")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
